// src/utils/FamilyOfficeConsolidatedExport.js
// Exports consolidés multi-familles pour le compte Family Office :
//   - exportConsolidatedCsv : une ligne par œuvre, toutes familles (structure Annexe A du manuel)
//   - generateConsolidatedPdf : rapport patrimonial consolidé (page de garde, synthèse,
//     ventilation par famille, top artistes, œuvres exceptionnelles, disclaimer)
//
// Les deux se branchent sur les données déjà chargées par FamilyOfficeDashboard
// (families, clientFamilyMap, collections, artworks) — aucune requête supplémentaire.

import jsPDF from "jspdf";
import Papa from "papaparse";
import { saveAs } from "file-saver";

// ─── Helpers valeur / format ────────────────────────────────────────────────
function artVal(a) {
  return (
    parseFloat(a.current_estimated_value) ||
    parseFloat(a.estimated_value) ||
    parseFloat(a.value) ||
    parseFloat(a.valuation_high) ||
    parseFloat(a.acquisition_price_eur) ||
    parseFloat(a.acquisition_price) ||
    0
  );
}
function num(v) {
  const n = parseFloat(v);
  return isNaN(n) ? "" : Math.round(n);
}
function formatEur(v) {
  if (v == null || v === "" || isNaN(parseFloat(v))) return "—";
  const n = Math.round(parseFloat(v));
  const formatted = Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return (n < 0 ? "-" : "") + formatted + " €";
}
function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}
function dimensionsOf(a) {
  if (a.dimensions) return String(a.dimensions);
  const parts = [a.height, a.width, a.depth].filter((x) => x != null && x !== "");
  if (!parts.length) return "";
  return parts.join(" × ") + (a.unit ? " " + a.unit : " cm");
}
function foName(foProfile) {
  return (
    foProfile?.company?.trim() ||
    [foProfile?.first_name, foProfile?.last_name].filter(Boolean).join(" ") ||
    "Family Office"
  );
}

// ─── Construction des lignes consolidées (une par œuvre) ────────────────────
// Retourne { rows, familyOfName } — familyOfName mappe artwork.id -> nom de famille
export function buildConsolidatedRows({ families = [], clientFamilyMap = {}, artworks = [] }) {
  const familyById = Object.fromEntries(families.map((f) => [f.id, f.label || "Famille"]));

  const rows = artworks.map((a) => {
    const familyId = clientFamilyMap[a.client_id];
    const family_name = familyById[familyId] || "—";
    return {
      family_name,
      inventory_number: a.inventory_number || "",
      title: a.title || "",
      artist: a.artist || "",
      year: a.year || "",
      medium: a.medium || "",
      dimensions: dimensionsOf(a),
      condition: a.condition || "",
      valuation_low: num(a.valuation_low),
      current_estimated_value: num(a.current_estimated_value ?? a.estimated_value ?? a.value),
      valuation_high: num(a.valuation_high),
      acquisition_price_eur: num(a.acquisition_price_eur ?? a.acquisition_price),
      acquisition_date: a.acquisition_date || "",
      ownership_status: a.ownership_status || "full",
      is_exceptional: a.is_exceptional ? "true" : "false",
    };
  });

  return rows;
}

// ─── Export CSV consolidé (structure Annexe A) ──────────────────────────────
export function exportConsolidatedCsv({ foProfile, families, clientFamilyMap, artworks }) {
  const rows = buildConsolidatedRows({ families, clientFamilyMap, artworks });

  // Ordre de colonnes = Annexe A du manuel Family Office
  const columns = [
    "family_name",
    "inventory_number",
    "title",
    "artist",
    "year",
    "medium",
    "dimensions",
    "condition",
    "valuation_low",
    "current_estimated_value",
    "valuation_high",
    "acquisition_price_eur",
    "acquisition_date",
    "location",
    "ownership_status",
    "is_exceptional",
  ];

  // location n'est pas exposée dans artworks_secure → colonne présente mais vide
  const data = rows.map((r) => ({ ...r, location: r.location || "" }));

  // Tri par famille puis artiste pour une lecture naturelle
  data.sort(
    (x, y) =>
      x.family_name.localeCompare(y.family_name, "fr") ||
      String(x.artist).localeCompare(String(y.artist), "fr")
  );

  const csv = Papa.unparse({ fields: columns, data }, { delimiter: ";" });
  // BOM UTF-8 pour Excel (accents corrects)
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const stamp = new Date().toISOString().slice(0, 10);
  const safeName = foName(foProfile).replace(/[^\w-]+/g, "_");
  saveAs(blob, `Artkiv_export_consolide_${safeName}_${stamp}.csv`);

  return data.length;
}

// ─── Palette PDF (alignée sur FamilyDetailPdfExport) ─────────────────────────
const C = {
  black: [26, 26, 26],
  gold: [201, 169, 97],
  cream: [249, 247, 244],
  lgray: [232, 232, 232],
  mgray: [102, 102, 102],
  dgray: [68, 68, 68],
  white: [255, 255, 255],
};
const fill = (p, c) => p.setFillColor(...c);
const stroke = (p, c) => p.setDrawColor(...c);
const tc = (p, c) => p.setTextColor(...c);

const MT = 34, MB = 16;

function drawHeader(pdf, foProfile, pageW) {
  fill(pdf, C.black); pdf.rect(0, 0, pageW, 24, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(10); tc(pdf, C.white);
  pdf.text(foName(foProfile), 12, 10);
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(7.5); tc(pdf, C.gold);
  pdf.text("Rapport consolidé · Family Office · Artkiv", 12, 17);
  pdf.setFontSize(7); tc(pdf, C.gold);
  pdf.text(`Édité le ${fmtDate(new Date().toISOString())}`, pageW - 12, 17, { align: "right" });
  fill(pdf, C.gold); pdf.rect(0, 24, pageW, 0.6, "F");
}
function drawFooter(pdf, n, pageW, pageH) {
  fill(pdf, C.lgray); pdf.rect(0, pageH - 10, pageW, 10, "F");
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
  pdf.text("Document confidentiel — Usage interne · Artkiv", 12, pageH - 4);
  pdf.text(`Page ${n}`, pageW - 12, pageH - 4, { align: "right" });
}
function secTitle(pdf, label, y, pageW) {
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(8.5); tc(pdf, C.mgray);
  pdf.text(label.toUpperCase(), 12, y);
  stroke(pdf, C.lgray); pdf.setLineWidth(0.25);
  pdf.line(12, y + 1.5, pageW - 12, y + 1.5);
  return y + 7;
}
function drawKpis(pdf, kpis, y, pageW) {
  const w = (pageW - 24) / kpis.length;
  kpis.forEach((k, i) => {
    const x = 12 + i * w;
    fill(pdf, C.cream); pdf.rect(x, y, w - 2, 18, "F");
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); tc(pdf, C.mgray);
    pdf.text(k.label, x + 3, y + 5);
    pdf.setFont("helvetica", "bold"); pdf.setFontSize(11); tc(pdf, C.black);
    pdf.text(String(k.value ?? "—"), x + 3, y + 13);
    if (k.sub) { pdf.setFont("helvetica", "normal"); pdf.setFontSize(6); tc(pdf, C.mgray); pdf.text(k.sub, x + 3, y + 16.5); }
  });
  return y + 22;
}
function truncate(pdf, str, maxMm) {
  if (str == null || str === "") return "—";
  str = String(str);
  const approxCharW = 7 * 0.45 / pdf.internal.scaleFactor;
  const max = Math.floor(maxMm / approxCharW);
  return str.length <= max ? str : str.substring(0, max - 1) + "…";
}
function drawTable(pdf, headers, rows, y, pageW, colWidths, rowH = 6.5) {
  const tW = pageW - 24;
  fill(pdf, C.black); pdf.rect(12, y, tW, 7, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); tc(pdf, C.white);
  let x = 12;
  headers.forEach((h, i) => { pdf.text(h, x + 2, y + 5); x += colWidths[i]; });
  y += 7;
  rows.forEach((row, ri) => {
    if (ri % 2 === 0) { fill(pdf, C.cream); pdf.rect(12, y, tW, rowH, "F"); }
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(7); tc(pdf, C.dgray);
    x = 12;
    row.forEach((cell, ci) => {
      pdf.text(truncate(pdf, cell, colWidths[ci] - 4), x + 2, y + 4.5);
      x += colWidths[ci];
    });
    y += rowH;
  });
  return y + 3;
}

// ─── Rapport PDF consolidé ──────────────────────────────────────────────────
export function generateConsolidatedPdf({ foProfile, families = [], clientFamilyMap = {}, artworks = [] }) {
  const pdf = new jsPDF("p", "mm", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  let page = 1;

  const rows = buildConsolidatedRows({ families, clientFamilyMap, artworks });
  const totalValue = artworks.reduce((s, a) => s + artVal(a), 0);
  const totalLow = artworks.reduce((s, a) => s + (parseFloat(a.valuation_low) || artVal(a)), 0);
  const totalHigh = artworks.reduce((s, a) => s + (parseFloat(a.valuation_high) || artVal(a)), 0);
  const nbFamilies = new Set(rows.map((r) => r.family_name).filter((n) => n && n !== "—")).size;
  const exceptional = artworks.filter((a) => a.is_exceptional);

  // ── Page de garde ──────────────────────────────────────────────────────────
  fill(pdf, C.black); pdf.rect(0, 0, pageW, pageH, "F");
  fill(pdf, C.gold); pdf.rect(0, 118, pageW, 0.8, "F");
  pdf.setFont("helvetica", "bold"); pdf.setFontSize(26); tc(pdf, C.white);
  pdf.text("Rapport patrimonial", pageW / 2, 95, { align: "center" });
  pdf.text("consolidé", pageW / 2, 108, { align: "center" });
  pdf.setFont("helvetica", "normal"); pdf.setFontSize(13); tc(pdf, C.gold);
  pdf.text(foName(foProfile), pageW / 2, 133, { align: "center" });
  pdf.setFontSize(9); tc(pdf, C.lgray);
  pdf.text(`${nbFamilies} famille${nbFamilies > 1 ? "s" : ""} · ${artworks.length} œuvre${artworks.length > 1 ? "s" : ""}`, pageW / 2, 145, { align: "center" });
  pdf.text(`Édité le ${fmtDate(new Date().toISOString())}`, pageW / 2, 152, { align: "center" });
  pdf.setFontSize(7.5); tc(pdf, C.mgray);
  pdf.text("Document confidentiel — Usage interne", pageW / 2, pageH - 20, { align: "center" });

  // ── Page 2 : synthèse ────────────────────────────────────────────────────────
  pdf.addPage(); page++;
  drawHeader(pdf, foProfile, pageW);
  let y = MT;

  y = secTitle(pdf, "Synthèse patrimoniale", y, pageW);
  y = drawKpis(pdf, [
    { label: "Valorisation totale", value: formatEur(totalValue) },
    { label: "Fourchette basse", value: formatEur(totalLow) },
    { label: "Fourchette haute", value: formatEur(totalHigh) },
  ], y, pageW);
  y = drawKpis(pdf, [
    { label: "Familles", value: nbFamilies },
    { label: "Œuvres", value: artworks.length },
    { label: "Œuvres exceptionnelles", value: exceptional.length },
  ], y, pageW);

  // Ventilation par famille
  y += 2;
  y = secTitle(pdf, "Ventilation par famille", y, pageW);
  const byFamily = {};
  artworks.forEach((a) => {
    const fam = families.find((f) => f.id === clientFamilyMap[a.client_id])?.label || "—";
    if (!byFamily[fam]) byFamily[fam] = { count: 0, value: 0 };
    byFamily[fam].count++;
    byFamily[fam].value += artVal(a);
  });
  const famRows = Object.entries(byFamily)
    .sort((a, b) => b[1].value - a[1].value)
    .map(([fam, d]) => [
      fam,
      String(d.count),
      formatEur(d.value),
      totalValue > 0 ? Math.round((d.value / totalValue) * 100) + " %" : "—",
    ]);
  y = drawTable(pdf, ["Famille", "Œuvres", "Valorisation", "Part"], famRows, y, pageW, [80, 24, 50, 32]);

  // Top artistes
  if (y + 40 > pageH - MB) { drawFooter(pdf, page, pageW, pageH); pdf.addPage(); page++; drawHeader(pdf, foProfile, pageW); y = MT; }
  y += 2;
  y = secTitle(pdf, "Top artistes (par valeur)", y, pageW);
  const byArtist = {};
  artworks.forEach((a) => {
    const art = a.artist || "Artiste inconnu";
    if (!byArtist[art]) byArtist[art] = { count: 0, value: 0 };
    byArtist[art].count++;
    byArtist[art].value += artVal(a);
  });
  const artistRows = Object.entries(byArtist)
    .sort((a, b) => b[1].value - a[1].value)
    .slice(0, 10)
    .map(([art, d]) => [art, String(d.count), formatEur(d.value)]);
  y = drawTable(pdf, ["Artiste", "Œuvres", "Valorisation"], artistRows, y, pageW, [110, 26, 50]);

  // Œuvres exceptionnelles
  if (exceptional.length) {
    if (y + 40 > pageH - MB) { drawFooter(pdf, page, pageW, pageH); pdf.addPage(); page++; drawHeader(pdf, foProfile, pageW); y = MT; }
    y += 2;
    y = secTitle(pdf, "Œuvres exceptionnelles — traitement successoral prioritaire", y, pageW);
    const excRows = exceptional
      .sort((a, b) => artVal(b) - artVal(a))
      .map((a) => {
        const fam = families.find((f) => f.id === clientFamilyMap[a.client_id])?.label || "—";
        return [fam, a.title || "Sans titre", a.artist || "—", formatEur(artVal(a))];
      });
    y = drawTable(pdf, ["Famille", "Œuvre", "Artiste", "Valorisation"], excRows, y, pageW, [55, 60, 46, 25]);
  }

  // Disclaimer
  if (y + 30 > pageH - MB) { drawFooter(pdf, page, pageW, pageH); pdf.addPage(); page++; drawHeader(pdf, foProfile, pageW); y = MT; }
  y += 3;
  const disclaimer =
    "Les valorisations sont indicatives, établies à partir des estimations renseignées sur chaque fiche œuvre. " +
    "Elles ne constituent pas une expertise formelle et ne sont pas opposables à l'administration fiscale. " +
    "Pour toute déclaration IFI, donation ou succession, une expertise signée par un professionnel agréé est indispensable.";
  const lines = pdf.splitTextToSize(disclaimer, pageW - 32);
  const h = lines.length * 4.2 + 8;
  fill(pdf, C.cream); pdf.rect(12, y, pageW - 24, h, "F");
  fill(pdf, C.gold); pdf.rect(12, y, 1.5, h, "F");
  pdf.setFont("helvetica", "italic"); pdf.setFontSize(7.5); tc(pdf, C.dgray);
  pdf.text(lines, 17, y + 6);

  drawFooter(pdf, page, pageW, pageH);

  const stamp = new Date().toISOString().slice(0, 10);
  const safeName = foName(foProfile).replace(/[^\w-]+/g, "_");
  pdf.save(`Artkiv_rapport_consolide_${safeName}_${stamp}.pdf`);
}
